import { ConversationMessage } from '../types/chat'
import { ResumeMemory } from '../types/memory'

const MEMORY_KEY = 'ivycv_user_memory'
const MESSAGES_KEY = 'ivycv_chat_messages'
const LATEX_KEY = 'ivycv_user_latex'
const EXTRACTED_CV_KEY = 'ivycv_extracted_cv'

// Extracted CV (text from uploaded PDF)

export interface ExtractedCV {
  filename: string
  text: string
}

export function loadExtractedCV(): ExtractedCV | null {
  try {
    const stored = localStorage.getItem(EXTRACTED_CV_KEY)
    return stored ? JSON.parse(stored) : null
  } catch {
    return null
  }
}

export function saveExtractedCV(filename: string, text: string): void {
  try {
    const cv: ExtractedCV = { filename, text }
    localStorage.setItem(EXTRACTED_CV_KEY, JSON.stringify(cv))
  } catch (e) {
    console.error('Failed to save extracted CV:', e)
  }
}

export function clearExtractedCV(): void {
  try {
    localStorage.removeItem(EXTRACTED_CV_KEY)
  } catch (e) {
    console.error('Failed to clear extracted CV:', e)
  }
}

// Memory (user profile text)

export function loadMemory(): string {
  try {
    return localStorage.getItem(MEMORY_KEY) || ''
  } catch {
    return ''
  }
}

export function saveMemory(memory: string): void {
  try {
    localStorage.setItem(MEMORY_KEY, memory)
  } catch (e) {
    console.error('Failed to save memory:', e)
  }
}

export function clearMemory(): void {
  try {
    localStorage.removeItem(MEMORY_KEY)
  } catch (e) {
    console.error('Failed to clear memory:', e)
  }
}

// Structured memory parsing and serialization

/**
 * Parse memory text (from Claude) into structured format.
 * This is best-effort parsing that won't fail on unexpected formats.
 */
export function parseMemoryToStructured(memoryText: string): ResumeMemory {
  const structured: ResumeMemory = {
    target: {
      role: null,
      companyTier: null,
      industry: null,
      level: null,
    },
    contact: {
      name: null,
      email: null,
      phone: null,
      linkedin: null,
      github: null,
      location: null,
    },
    education: [],
    experience: [],
    skills: {
      languages: [],
      frameworks: [],
      tools: [],
    },
    coachingNotes: [],
  }

  if (!memoryText || memoryText.trim().length === 0) {
    return structured
  }

  try {
    const lines = memoryText.split('\n')
    let currentSection = ''
    let currentExperience: any = null

    for (const line of lines) {
      const trimmed = line.trim()
      if (!trimmed) continue

      // Detect sections
      if (trimmed.startsWith('TARGET:')) {
        currentSection = 'target'
        continue
      } else if (trimmed.startsWith('CONTACT:')) {
        currentSection = 'contact'
        continue
      } else if (trimmed.startsWith('EDUCATION:')) {
        currentSection = 'education'
        continue
      } else if (trimmed.startsWith('EXPERIENCE:')) {
        currentSection = 'experience'
        continue
      } else if (trimmed.startsWith('SKILLS:')) {
        currentSection = 'skills'
        continue
      } else if (trimmed.startsWith('NOTES:') || trimmed.startsWith('COACHING NOTES:')) {
        currentSection = 'notes'
        continue
      }

      // Parse based on current section
      if (currentSection === 'target') {
        if (trimmed.startsWith('- Role:')) {
          structured.target.role = extractValue(trimmed) || null
        } else if (trimmed.startsWith('- Companies:') || trimmed.startsWith('- Tier:')) {
          const value = extractValue(trimmed)?.toLowerCase()
          if (value) {
            if (value.includes('faang')) structured.target.companyTier = 'faang'
            else if (value.includes('bigtech') || value.includes('big tech')) structured.target.companyTier = 'bigtech'
            else if (value.includes('startup')) structured.target.companyTier = 'startup'
            else if (value.includes('enterprise')) structured.target.companyTier = 'enterprise'
            else if (value.includes('consulting')) structured.target.companyTier = 'consulting'
            else if (value.includes('finance')) structured.target.companyTier = 'finance'
            else structured.target.companyTier = 'other'
          }
        } else if (trimmed.startsWith('- Industry:')) {
          structured.target.industry = extractValue(trimmed) || null
        } else if (trimmed.startsWith('- Level:')) {
          const value = extractValue(trimmed)?.toLowerCase()
          if (value) {
            if (value.includes('entry')) structured.target.level = 'entry'
            else if (value.includes('mid')) structured.target.level = 'mid'
            else if (value.includes('senior')) structured.target.level = 'senior'
            else if (value.includes('exec')) structured.target.level = 'executive'
          }
        }
      } else if (currentSection === 'contact') {
        if (trimmed.startsWith('- Name:')) {
          structured.contact.name = extractValue(trimmed) || null
        } else if (trimmed.startsWith('- Email:')) {
          structured.contact.email = extractValue(trimmed) || null
        } else if (trimmed.startsWith('- Phone:')) {
          structured.contact.phone = extractValue(trimmed) || null
        } else if (trimmed.startsWith('- LinkedIn:')) {
          structured.contact.linkedin = extractValue(trimmed) || null
        } else if (trimmed.startsWith('- GitHub:')) {
          structured.contact.github = extractValue(trimmed) || null
        } else if (trimmed.startsWith('- Location:')) {
          structured.contact.location = extractValue(trimmed) || null
        }
      } else if (currentSection === 'education') {
        // Format: "- [Degree] | [School] | [Dates] | [GPA/Honors]"
        if (trimmed.startsWith('-')) {
          const parts = trimmed.substring(1).split('|').map(p => p.trim())
          if (parts.length >= 3) {
            const edu: any = {
              degree: parts[0],
              school: parts[1],
              dates: parts[2],
            }
            if (parts.length > 3 && parts[3]) {
              if (parts[3].toLowerCase().includes('gpa')) {
                edu.gpa = parts[3]
              } else {
                edu.honors = [parts[3]]
              }
            }
            structured.education.push(edu)
          }
        }
      } else if (currentSection === 'experience') {
        // Company header format: "[Company] | [Title] | [Dates]"
        if (!trimmed.startsWith('-') && trimmed.includes('|')) {
          // Save previous experience if exists
          if (currentExperience) {
            structured.experience.push(currentExperience)
          }
          const parts = trimmed.split('|').map(p => p.trim())
          if (parts.length >= 3) {
            currentExperience = {
              company: parts[0],
              title: parts[1],
              dates: parts[2],
              location: parts.length > 3 ? parts[3] : undefined,
              bullets: [],
            }
          }
        } else if (trimmed.startsWith('-') && currentExperience) {
          // Bullet format: "- [Bullet text] [STATUS: strong/needs-work/weak]"
          let bulletText = trimmed.substring(1).trim()
          let quality: 'strong' | 'needs-work' | 'weak' = 'needs-work'

          // Extract quality indicator if present
          const statusMatch = bulletText.match(/\[STATUS:\s*(strong|needs-work|weak|pending-metrics)\]/i)
          if (statusMatch) {
            const status = statusMatch[1].toLowerCase()
            if (status === 'strong') quality = 'strong'
            else if (status === 'weak' || status === 'pending-metrics') quality = 'weak'
            else quality = 'needs-work'
            bulletText = bulletText.replace(statusMatch[0], '').trim()
          } else {
            // Infer quality from content
            quality = inferBulletQuality(bulletText)
          }

          currentExperience.bullets.push({ text: bulletText, quality })
        }
      } else if (currentSection === 'skills') {
        if (trimmed.startsWith('- Languages:')) {
          structured.skills.languages = extractList(trimmed)
        } else if (trimmed.startsWith('- Frameworks:')) {
          structured.skills.frameworks = extractList(trimmed)
        } else if (trimmed.startsWith('- Tools:')) {
          structured.skills.tools = extractList(trimmed)
        }
      } else if (currentSection === 'notes') {
        if (trimmed.startsWith('-')) {
          structured.coachingNotes.push(trimmed.substring(1).trim())
        } else {
          structured.coachingNotes.push(trimmed)
        }
      }
    }

    // Don't forget to add the last experience
    if (currentExperience) {
      structured.experience.push(currentExperience)
    }
  } catch (error) {
    console.warn('Error parsing memory, returning partial result:', error)
  }

  return structured
}

/**
 * Serialize structured memory back to text format for Claude.
 * This maintains the human-readable format Claude expects.
 */
export function serializeStructuredMemory(structured: ResumeMemory): string {
  const lines: string[] = []

  // TARGET section
  if (structured.target.role || structured.target.companyTier || structured.target.industry || structured.target.level) {
    lines.push('TARGET:')
    if (structured.target.role) lines.push(`- Role: ${structured.target.role}`)
    if (structured.target.companyTier) lines.push(`- Companies: ${structured.target.companyTier}`)
    if (structured.target.industry) lines.push(`- Industry: ${structured.target.industry}`)
    if (structured.target.level) lines.push(`- Level: ${structured.target.level}`)
    lines.push('')
  }

  // CONTACT section
  if (Object.values(structured.contact).some(v => v !== null)) {
    lines.push('CONTACT:')
    if (structured.contact.name) lines.push(`- Name: ${structured.contact.name}`)
    if (structured.contact.email) lines.push(`- Email: ${structured.contact.email}`)
    if (structured.contact.phone) lines.push(`- Phone: ${structured.contact.phone}`)
    if (structured.contact.linkedin) lines.push(`- LinkedIn: ${structured.contact.linkedin}`)
    if (structured.contact.github) lines.push(`- GitHub: ${structured.contact.github}`)
    if (structured.contact.location) lines.push(`- Location: ${structured.contact.location}`)
    lines.push('')
  }

  // EDUCATION section
  if (structured.education.length > 0) {
    lines.push('EDUCATION:')
    structured.education.forEach(edu => {
      const parts = [edu.degree, edu.school, edu.dates]
      if (edu.gpa) parts.push(edu.gpa)
      else if (edu.honors && edu.honors.length > 0) parts.push(edu.honors.join(', '))
      lines.push(`- ${parts.join(' | ')}`)
    })
    lines.push('')
  }

  // EXPERIENCE section
  if (structured.experience.length > 0) {
    lines.push('EXPERIENCE:')
    structured.experience.forEach(exp => {
      const parts = [exp.company, exp.title, exp.dates]
      if (exp.location) parts.push(exp.location)
      lines.push(parts.join(' | '))
      exp.bullets.forEach(bullet => {
        lines.push(`- ${bullet.text} [STATUS: ${bullet.quality}]`)
      })
      lines.push('')
    })
  }

  // SKILLS section
  if (structured.skills.languages.length > 0 || structured.skills.frameworks.length > 0 || structured.skills.tools.length > 0) {
    lines.push('SKILLS:')
    if (structured.skills.languages.length > 0) {
      lines.push(`- Languages: ${structured.skills.languages.join(', ')}`)
    }
    if (structured.skills.frameworks.length > 0) {
      lines.push(`- Frameworks: ${structured.skills.frameworks.join(', ')}`)
    }
    if (structured.skills.tools.length > 0) {
      lines.push(`- Tools: ${structured.skills.tools.join(', ')}`)
    }
    lines.push('')
  }

  // NOTES section
  if (structured.coachingNotes.length > 0) {
    lines.push('NOTES:')
    structured.coachingNotes.forEach(note => {
      lines.push(`- ${note}`)
    })
  }

  return lines.join('\n')
}

// Helper functions for parsing

function extractValue(line: string): string | null {
  const colonIndex = line.indexOf(':')
  if (colonIndex === -1) return null
  const value = line.substring(colonIndex + 1).trim()
  return value.length > 0 ? value : null
}

function extractList(line: string): string[] {
  const value = extractValue(line)
  if (!value) return []
  return value.split(',').map(item => item.trim()).filter(item => item.length > 0)
}

function inferBulletQuality(bulletText: string): 'strong' | 'needs-work' | 'weak' {
  // Strong bullets typically have:
  // - Numbers/metrics (%, $, M, K, etc.)
  // - Specific outcomes
  // - Action verbs
  const hasMetrics = /\d+[%$MKB]|\d+\+|\d{1,3}(,\d{3})*/.test(bulletText)
  const hasStrongVerbs = /^(Architected|Built|Led|Designed|Implemented|Launched|Shipped|Developed|Reduced|Increased|Optimized|Drove)/i.test(bulletText)
  const isLongEnough = bulletText.length > 50

  if (hasMetrics && hasStrongVerbs && isLongEnough) {
    return 'strong'
  } else if (hasMetrics || hasStrongVerbs) {
    return 'needs-work'
  } else {
    return 'weak'
  }
}

// Conversation messages

export function loadMessages(): ConversationMessage[] {
  try {
    const stored = localStorage.getItem(MESSAGES_KEY)
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

export function saveMessages(messages: ConversationMessage[]): void {
  try {
    localStorage.setItem(MESSAGES_KEY, JSON.stringify(messages))
  } catch (e) {
    console.error('Failed to save messages:', e)
  }
}

export function clearMessages(): void {
  try {
    localStorage.removeItem(MESSAGES_KEY)
  } catch (e) {
    console.error('Failed to clear messages:', e)
  }
}

// LaTeX source (user's CV)

export function loadLatex(): string | null {
  try {
    return localStorage.getItem(LATEX_KEY)
  } catch {
    return null
  }
}

export function saveLatex(latex: string): void {
  try {
    localStorage.setItem(LATEX_KEY, latex)
  } catch (e) {
    console.error('Failed to save latex:', e)
  }
}

export function clearLatex(): void {
  try {
    localStorage.removeItem(LATEX_KEY)
  } catch (e) {
    console.error('Failed to clear latex:', e)
  }
}

// Clear all data

export function clearAll(): void {
  clearMemory()
  clearMessages()
  clearLatex()
  clearExtractedCV()
}
